//Libraries
import axios from "axios";
import CryptoJS from "crypto-js";
import { BigNumber, ethers, providers, Wallet } from "ethers";
import w3Abi, { AbiCoder } from "web3-eth-abi";

// Firebase
import { FirebaseApp, initializeApp } from "firebase/app";
import { Auth, getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { Database, getDatabase } from "firebase/database";
import {
  collection,
  doc,
  Firestore,
  getDoc,
  getDocs,
  initializeFirestore,
  query,
  where,
} from "firebase/firestore";
import { FirebaseStorage, getStorage } from "firebase/storage";

//Utils
import { logger } from "./common/logger";
import { capitalizeFirstLetter, getTransactionData } from "./common/utils";
import * as Hash from "./modules/hash";

//Constants
import {
  ERROR_TYPE,
  ERROR_TYPE_V1,
  IPFS_GATEWAY,
  NFT_ATTRIBUTES,
} from "./common/constants";

//Abis
import addCategoriesAbi from "./common/abis/AddCategories";

//Config
import config from "./config";

//Types
import { maxBy } from "lodash";
import { getDocument, getDocuments } from "./common/firestore";
import { generateMerkleRoot } from "./common/merkleTree";
import { generateSigners, getRTDBSigners } from "./common/signers";
import { storeAttributedData, uploadNftsIpfs } from "./common/upload";
import { decryptWallet, signMessage } from "./common/wallet";
import {
  ContractStruct,
  EnvV1,
  MetaTransaction,
  Nft,
  Optionals,
  SdkError,
  SdkV1Options,
  User,
} from "./types";

function getWeb3Abi(w3Abi: unknown): AbiCoder {
  return w3Abi as AbiCoder;
}

const web3Abi = getWeb3Abi(w3Abi);

export class BcodeSDKV1 {
  email: string;
  password: string;
  authToken: string;
  env: EnvV1;
  initialized: boolean;
  version: string;
  contracts: { [key: string]: string };
  user!: User;
  provider!: providers.JsonRpcProvider;
  public wallet!: Wallet;
  public lastNonce: BigNumber;

  // Firebase components
  app: FirebaseApp;
  messagesApp: FirebaseApp;
  auth: Auth;
  db: Firestore;
  database: Database;
  dbMessages: Database;
  storage: FirebaseStorage;

  constructor(sdkOptions: SdkV1Options) {
    this.version = sdkOptions.version || "v1";
    this.initialized = false;
    this.env = sdkOptions.config?.env || "DEV";

    this.email = sdkOptions.email || "";
    this.password = sdkOptions.password || "";
    this.authToken = sdkOptions.authToken || "";

    this.contracts = {
      BCODE_META_TRANSACTION: config.V1[this.env].BCODE_META_TRANSACTION,
    };

    this.provider = new ethers.providers.JsonRpcProvider(
      sdkOptions.config.rpcProvider || config.V1[this.env].RPC_PROVIDER
    );
    this.lastNonce = BigNumber.from(0);

    if (!sdkOptions.config?.debugMode) {
      // logger.transports[0].silent = true;
      logger.info = () => {};
      logger.error = () => {};
    }

    const firebaseConfig = {
      apiKey: config.V1[this.env].APIKEY,
      authDomain: config.V1[this.env].AUTHDOMAIN,
      projectId: config.V1[this.env].PROJECTID,
      storageBucket: config.V1[this.env].STORAGE_BUCKET,
      messaggingSenderId: config.V1[this.env].MESSAGGING_SENDER_ID,
      appId: config.V1[this.env].APP_ID,
      databaseURL: config.V1[this.env].RTDB_FIREBASE,
    };

    this.app = initializeApp(firebaseConfig);
    this.messagesApp = initializeApp(
      {
        ...firebaseConfig,
        databaseURL: config.V1[this.env].RTDB_FIREBASE_MESSAGE,
      },
      "messagesApp"
    );

    this.auth = getAuth(this.app);
    this.db = initializeFirestore(this.app, {
      experimentalForceLongPolling: true,
    });
    this.database = getDatabase(this.app);
    this.dbMessages = getDatabase(this.messagesApp);
    this.storage = getStorage(this.app);

    logger.info(`Working environment: ${this.env}`);
    logger.info("Finished initialization");
  }

  /**
   * Need to called once after calling constructor
   */
  async init() {
    try {
      if (this.email && this.password) {
        try {
          await this.loginUser();
        } catch (e) {
          logger.error(`[Init] Error: ${e}`);
          throw ERROR_TYPE_V1.WRONG_CREDENTIALS;
        }
      } else if (this.authToken) {
        this.checkJWTValidity();
      }

      this.initialized = true;
    } catch (error) {
      logger.info("[Error] ", error);
      throw error;
    }
  }

  private async loginUser() {
    const { user } = await signInWithEmailAndPassword(
      this.auth,
      this.email,
      CryptoJS.SHA1(this.password).toString()
    );

    const docRef = doc(this.db, "users", user.uid);
    const docSnapshot = await getDoc(docRef);

    let profile;

    if (docSnapshot.exists()) {
      profile = docSnapshot.data();
      this.user = profile as User;

      if (this.user.authTokens) {
        this.authToken = this.user.authTokens[0];
      }
    }

    this.wallet = decryptWallet(profile?.wallet, this.password);
    this.lastNonce = await this.getNonce(this.wallet.address);
  }

  isInitialized() {
    return this.initialized;
  }

  /**
   * @description Return JWT token
   *
   * @returns authToken
   */
  getAuthToken() {
    logger.info(`Your auth token is: ${this.authToken}`);

    if (this.authToken) {
      return this.authToken;
    } else {
      logger.error("[Error] You are using your email/password!");
      return null;
    }
  }

  /**
   * @description Return address of wallet associated in current instance
   *
   * @returns wallet address
   */
  getWalletAddress() {
    return this.wallet!.address;
  }

  getWallet() {
    return this.wallet;
  }

  /**
   * @desciption Return wallet private key
   *
   * @returns privateKey
   */
  getPrivateKey() {
    return this.wallet!.privateKey;
  }

  /**
   * @description Build and return ethers Contract object
   *
   * @param address
   * @param abi
   * @returns ethers.Contract object
   */
  getContract(address: string, abi: any[]) {
    return new ethers.Contract(address, abi, this.provider);
  }

  /**
   * @description Return nonce of meta transaction contract, different from blockchain nonce
   *
   * @param address
   * @returns
   */
  async getNonce(address: string) {
    const metaTxContract = this.getContract(
      this.contracts.BCODE_META_TRANSACTION,
      [
        {
          inputs: [
            {
              internalType: "address",
              name: "user",
              type: "address",
            },
          ],
          name: "getNonce",
          outputs: [
            {
              internalType: "uint256",
              name: "nonce",
              type: "uint256",
            },
          ],
          stateMutability: "view",
          type: "function",
        },
      ]
    );

    return await metaTxContract.getNonce(address);
  }

  private async getNextNonce() {
    const blockchainNonce = await this.getNonce(this.wallet!.address);

    if (blockchainNonce >= this.lastNonce) {
      return blockchainNonce;
    } else if (this.lastNonce > blockchainNonce) {
      return this.lastNonce.add(1);
    }
  }

  setLastNonce(nonce: number) {
    const nonceBn = BigNumber.from(nonce);

    if (nonceBn.gt(this.lastNonce)) {
      this.lastNonce = nonceBn;
    }
  }

  /**
   * @description Return user object to get profile data
   *
   * @returns User object
   */
  getUser() {
    return this.user;
  }

  /**
   * @description This function allow the creation of a meta transaction compatible signature.
   * The result of this function needs to be passed to executeTransaction
   *
   * @param contractObj
   * @param functionName
   * @param params
   * @param optionals The nonce reffered in optionals object is releated to the meta transaction nonce not to the
   * standard transaction nonce of the blockchain
   * @returns Transactin Object
   */
  async prepareTransaction(
    contractObj: ContractStruct,
    functionName: string,
    params: Array<any>,
    optionals?: { nonce: number }
  ): Promise<MetaTransaction> {
    logger.info(`[Prepare Transaction]`);
    logger.info(
      `${contractObj.address}\n${contractObj.name}\n${contractObj.version}\n${functionName}`
    );

    let functionSignature = web3Abi.encodeFunctionCall(
      contractObj.abi.find(
        (el) => el.type === "function" && el.name === functionName
      ),
      params
    );

    /**
     * Allow user to send more transaction at once by specifing future nonce
     */
    let nonce = optionals?.nonce || 0;
    if (!optionals?.nonce) {
      nonce = await this.getNextNonce();
    }

    logger.info(`[Prepare Transaction] Nonce: ${nonce}`);

    let { r, s, v } = await getTransactionData(
      nonce,
      functionSignature,
      this.wallet!.address,
      this.wallet!.privateKey,
      {
        name: contractObj.name,
        version: contractObj.version,
        address: contractObj.address,
        chainId: config.V1[this.env].CHAIN_ID,
      }
    );
    logger.info("[Prepare Transaction] Success");

    return {
      contractAddress: contractObj.address,
      userAddress: this.wallet!.address,
      functionSignature,
      nonce,
      r: `0x${r.toString("hex")}`,
      s: `0x${s.toString("hex")}`,
      v,
    };
  }

  /**
   * @description Send meta transaction signature to Bcode API to be processed.
   *
   * @param tx
   * @param optionals
   * @returns Request id
   */
  async executeTransaction(tx: MetaTransaction, optionals: Optionals) {
    try {
      this.setLastNonce(tx?.nonce || 0);
      delete tx.nonce;

      const { status, data } = await axios.post(
        `${config.V1[this.env].BACKEND_ENDPOINT}/sendRawTransactionAsync`,
        {
          tx,
          ...optionals,
        },
        { headers: { Authorization: `Bearer ${this.authToken}` } }
      );

      if (status === 200) {
        logger.info("[Execute Async Transaction] Success");
        return data.requestId;
      } else {
        logger.info(`[Execute Async Transaction] Receive status: ${status}`);
        return null;
      }
    } catch (err) {
      logger.error(`[Execute Async Transaction] Error: ${err}`);
      return null;
    }
  }

  /**
   * @description Request hash notarization to Bcode API
   *
   * @param hash
   * @param optionals
   * @returns Request id
   */
  async notarizeHash(values: {
    name: string;
    description: string;
    notes: string;
    tags: string[];
    storage?: "storage" | "ipfs";
    folderId?: string;
    fileList: Buffer[];
  }) {
    const {
      fileList,
      name,
      description,
      notes,
      tags,
      storage,
      folderId,
    } = values;

    try {
      if (!fileList.length) {
        throw ERROR_TYPE_V1.MISSING_PARAMS;
      }
      const file = fileList?.[0];

      const generatedHash = await Hash.calculateFileHash(file);
      const hash = generatedHash.startsWith("0x")
        ? generatedHash
        : `0x${generatedHash}`;

      const signature = await signMessage(hash, this.wallet);

      const { status, data } = await axios.post(
        `${config.V1[this.env].BACKEND_ENDPOINT}/api/notarize`,
        {
          hash,
          name,
          description,
          notes,
          tags,
          signature,
        },
        {
          headers: {
            Authorization: this.authToken,
          },
        }
      );

      if (status === 200) {
        logger.info(`[Notarize hash] Create notarization with id: ${data.id}`);
        return data.id;
      } else {
        logger.error(`Notarization error, status code ${status} `);
        throw {
          type: "UNABLE_TO_NOTARIZE",
          message: "Unable to ntoarize hash",
        };
      }
    } catch (e) {
      const error = e as SdkError;
      logger.error(`Notarization error: ${error} `);

      if (error?.type && error?.message) {
        throw e;
      }

      throw {
        type: "NOTARIZATION_ERROR",
        message: "Error during notarization",
      };
    }
  }

  /**
   * @description Function that return notarization data object with status
   * If the notarization is already notarized returns also the receipt
   *
   * @param docId
   * @returns Notarization data object
   */
  async getNotarization(docId: string) {
    const notarization = await getDocument({
      collectionName: "notarizations",
      docId,
      db: this.db,
    });

    if (!notarization) {
      throw ERROR_TYPE_V1.WRONG_CREDENTIALS;
    }

    const receipt = await getDocuments({
      db: this.db,
      collectionName: "receipts",
      constraint: where("receiptId", "==", notarization.receiptId),
    });

    if (!receipt) {
      return notarization;
    } else {
      return { ...notarization, receipt: receipt[0] };
    }
  }

  /**
   * @description This function handle the image upload and enpoint call
   * to add categories to a contract
   *
   * @param params  - Object containing parameters for function
   * @returns RequestId or throw error
   */
  async addCategories({
    nfts,
    contractId,
    workspaceId,
  }: {
    nfts: Nft[];
    contractId: string;
    workspaceId?: string;
  }) {
    let newNfts = nfts;

    if (!contractId || !nfts.length) {
      throw ERROR_TYPE_V1.MISSING_PARAMS;
    }

    const contract = await getDocument({
      collectionName: "contracts",
      docId: contractId,
      db: this.db,
    });

    if (!contract) {
      throw ERROR_TYPE_V1.CONTRACT_NOT_FOUND;
    }

    const oldNfts = (await getDocuments({
      collectionName: `contracts/${contractId}/nfts`,
      db: this.db,
    })) as Nft[];

    const querySnapshot = await getDocs(
      query(
        collection(this.db, `users/${this.user.uid}/workspaces`),
        where("is_owner", "==", true)
      )
    );

    const ownedWorkspaceId = querySnapshot.docs[0]?.id;

    try {
      newNfts = await Promise.all(
        await storeAttributedData(
          newNfts,
          { name: contract?.name, contractId },
          workspaceId || ownedWorkspaceId,
          this.env,
          this.authToken
        )
      );

      /**
       * Upload token metadata and image to IPFS, but only the public nft attributes
       */
      const { metadataCid, imagesCid } = await uploadNftsIpfs(
        [
          ...oldNfts,
          ...newNfts.map((nft) => ({
            ...nft,
            attributes: nft.attributes?.filter(
              (attribute) =>
                !attribute.private && NFT_ATTRIBUTES.includes(attribute.type)
            ),
          })),
        ],
        contract!.id,
        workspaceId || ownedWorkspaceId,
        this.env,
        this.authToken
      );

      // Get the updated data of the nfts that has changed
      const lastId = maxBy(nfts, "id")?.id || 0;

      newNfts = newNfts.map((nft, index) => {
        return {
          ...nft,
          image: `${IPFS_GATEWAY}/${imagesCid}/${nft.name}`,
          id: lastId + index + 1,
          quantity: 1,
        };
      });

      try {
        const signers = [
          ...(await getRTDBSigners({
            contractId: contract?.id,
            tokens: contract.maxSupplyPerRarity.length - 1,
            db: this.database,
          })),
          ...generateSigners(newNfts.map(({ quantity }) => quantity)),
        ];

        const merkleRoot = generateMerkleRoot(signers);

        const metaTx = await this.prepareTransaction(
          {
            address: contract!.address,
            abi: addCategoriesAbi,
            name: capitalizeFirstLetter(contract!.name).replace(/\s/g, ""),
            version: "1.0",
          },
          "addCategories",
          [merkleRoot, `${IPFS_GATEWAY}/${metadataCid}/`]
        );

        this.setLastNonce(metaTx?.nonce || 0);

        const {
          data: { requestId, error },
        } = await axios.post(
          `${config.V1[this.env].BACKEND_ENDPOINT}/addCategories`,
          {
            contractId,
            nfts: newNfts,
            metadataCid,
            signers: signers.slice(nfts.length),
            merkleRoot,
            metaTx,
            startIndex: oldNfts.length,
          },
          {
            headers: {
              authorization: this.authToken,
              contractId,
              workspaceId: workspaceId || ownedWorkspaceId,
            },
          }
        );

        if (error) {
          throw ERROR_TYPE_V1.ERROR_WHILE_EXECUTING_FUNCTION;
        } else {
          return requestId;
        }
      } catch (e) {
        console.error("INSIDE ERROR ==>", e);
        throw ERROR_TYPE_V1.ERROR_WHILE_EXECUTING_FUNCTION;
      }
    } catch (err) {
      console.error("OUTSIDE ERROR ==>", err);
      throw ERROR_TYPE_V1.ERROR_WHILE_EXECUTING_FUNCTION;
    }
  }

  /**
   * @description This functions outputs the NFT tokens, owned by the user, of the contract addresses given through the input
   *
   * @param contractAddresses
   * @param ownerAddress
   * @returns Object with contract addresses as keys and NFT arrays as value
   */
  // async getOwnedNFT(
  //   contractAddresses: string[],
  //   ownerAddress = this.wallet!.address
  // ) {
  //   let tokenOfOwner = {};

  //   for (const addr of contractAddresses) {
  //     let contract = new ethers.Contract(
  //       addr,
  //       BcodeNFT.abi,
  //       this.wallet!.connect(this.provider)
  //     );

  //     let balance = await contract.balanceOf(ownerAddress);

  //     logger.info(`User has ${balance} NFTs in ${addr} contract`);
  //     let tokenIds: {tokenId: string; tokenURI: string}[]= [];
  //     for (const i of range(balance)) {
  //       const tokenId = await contract.tokenOfOwnerByIndex(ownerAddress, i);

  //       // logger.info(`Token: ${await contract.baseURI()}`);

  //       // tokenOfOwner[addr] = parseInt(tokenId.values.toString());
  //       tokenIds.push({
  //         tokenId: tokenId.toString(),
  //         tokenURI: await contract.tokenURI(tokenId.toString()),
  //       });
  //     }

  //     tokenOfOwner[addr] = tokenIds;
  //     // logger.info(`User own from ${addr}: ${tokenOfOwner[addr]}`);
  //   }

  //   return tokenOfOwner;
  // }

  /**
   * @description Check if JWT Token is still valid
   *
   * @returns boolean
   */
  async checkJWTValidity() {
    try {
      let { status, data } = await axios.get(
        `${config.V1[this.env].BACKEND_ENDPOINT}/checkJWT`,
        {
          headers: {
            Authorization: `Bearer ${this.authToken}`,
          },
        }
      );

      return data.auth;
    } catch (error) {
      throw ERROR_TYPE.UNABLE_TO_CHECK_TOKEN;
    }
  }

  /**
   * @description Return API version
   *
   * @returns string
   */
  async getAPIVersion() {
    let { data } = await axios.get(
      `${config.V1[this.env].BACKEND_ENDPOINT}/getVersion`
    );
    return data;
  }

  /**
   * @description Return BcodeSDK version
   *
   * @returns string
   */
  getVersion() {
    return this.version;
  }
}
